import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useThrottledCallback } from "./useThrottledCallback";

import { setPageScrolled } from "../../reducers/styling";
import { useIsIOSWebkit } from "./useIsIOSWebkit";

// Use two different offsets for page scrolling up and down state change triggering to
// avoid collapsing and uncollapsing of layout elements, by triggering a page resize, to
// trigger a scroll state update loop.
const PAGE_SCROLLED_DOWN_Y_OFFSET = 250;
const PAGE_SCROLLED_UP_Y_OFFSET = 150;
// Throttle all scrolled actions, as they're just used to enhance reading confort of the
// page and are not crucial to happen exactly at the same moment when the scroll offset
// that changes the scrolled state happens.
const PAGE_SCROLLED_CALLBACKS_THROTTLING_DELAY = 100;

export const usePageScrolledObserver = appContainer => {
  const isPageScrolled = useSelector(({ styling }) => styling.isPageScrolled);
  const classUpdateTimeoutId = useRef();
  const dispatch = useDispatch();

  // Don't handle scrolled state on mobile Safari, as it doesn't support scroll events and
  // only triggers them when the keyboard opens, which creates bugs in the app.
  const isIOSWebkit = useIsIOSWebkit();

  const refreshIsPageScrolled = useThrottledCallback(
    () => {
      if (!appContainer || isIOSWebkit) return;

      const offsetY = window.pageYOffset || document.documentElement.scrollTop;

      if (classUpdateTimeoutId.current) clearTimeout(classUpdateTimeoutId.current);

      if (offsetY > PAGE_SCROLLED_DOWN_Y_OFFSET && !isPageScrolled) {
        classUpdateTimeoutId.current = setTimeout(
          () => dispatch(setPageScrolled(true)),
          PAGE_SCROLLED_CALLBACKS_THROTTLING_DELAY
        );
      } else if (offsetY <= PAGE_SCROLLED_UP_Y_OFFSET && isPageScrolled) {
        classUpdateTimeoutId.current = setTimeout(
          () => dispatch(setPageScrolled(false)),
          PAGE_SCROLLED_CALLBACKS_THROTTLING_DELAY
        );
      }
    },
    [appContainer, dispatch, isIOSWebkit, isPageScrolled],
    PAGE_SCROLLED_CALLBACKS_THROTTLING_DELAY
  );

  useLayoutEffect(() => {
    refreshIsPageScrolled();
    return () => refreshIsPageScrolled();
  }, [appContainer, refreshIsPageScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", refreshIsPageScrolled);
    return () => window.removeEventListener("scroll", refreshIsPageScrolled);
  }, [refreshIsPageScrolled]);
};
